import React, { useState } from 'react';
import { Alert, Button, Stack } from '@mui/material';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';

/** Remembered per browser: "Not now" is not asked again. */
export const NOTIFICATIONS_DISMISSED_KEY = 'supportChat.notificationsDismissed';

const permission = (): NotificationPermission | null =>
  typeof Notification === 'undefined' ? null : Notification.permission;

const wasDismissed = (): boolean => {
  try {
    return window.localStorage.getItem(NOTIFICATIONS_DISMISSED_KEY) === '1';
  } catch (_) {
    return false;
  }
};

/**
 * Asks once, from a click, for desktop notifications: with them the console
 * says when a rider writes while its tab is hidden. Shown only while the
 * browser has not been asked and the agent has not said "Not now".
 */
export function NotificationBanner() {
  const [state, setState] = useState(permission);
  const [dismissed, setDismissed] = useState(wasDismissed);

  if (state !== 'default' || dismissed) return null;

  const enable = async () => {
    try {
      const answer = await Notification.requestPermission();
      setState(answer);
    } catch (_) {
      setState(permission());
    }
  };
  const notNow = () => {
    try {
      window.localStorage.setItem(NOTIFICATIONS_DISMISSED_KEY, '1');
    } catch (_) {
      // Private mode: it is asked again next visit.
    }
    setDismissed(true);
  };

  return (
    <Alert
      severity="info"
      icon={<NotificationsActiveOutlinedIcon />}
      sx={{ mb: 2 }}
      data-testid="notification-banner"
      action={
        <Stack direction="row" gap={1}>
          <Button color="inherit" size="small" onClick={notNow}>
            Not now
          </Button>
          <Button variant="contained" size="small" onClick={enable}>
            Turn on
          </Button>
        </Stack>
      }
    >
      Turn on desktop notifications to know when a rider writes while this tab is in the
      background.
    </Alert>
  );
}
